// Native-only "update available" banner. The app binary bundles the whole
// frontend, so members only get new features via a store update — this nudges
// anyone on an old build toward their store listing. Driven entirely by the
// server's /app-version/ endpoint; when that's unset, nothing ever shows.
// Below min_version it becomes a full-screen block with no dismiss.

import { useEffect, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import { ArrowUpCircle, X } from 'lucide-react';
import { metaAPI } from '../api/client';
import { isNativePlatform, platform } from '../native';
import { isVersionOutdated } from '../lib/appVersion';

const DISMISS_KEY = 'update-nudge-dismissed';

export default function UpdateNudge() {
  const [storeUrl, setStoreUrl] = useState<string | null>(null);
  const [required, setRequired] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1',
  );

  useEffect(() => {
    if (!isNativePlatform) return;
    (async () => {
      try {
        const [meta, info] = await Promise.all([
          metaAPI.getAppVersion(),
          CapApp.getInfo(),
        ]);
        const belowMinimum = isVersionOutdated(info.version, meta.min_version ?? '');
        if (!belowMinimum && !isVersionOutdated(info.version, meta.latest_version)) return;
        const url = platform === 'ios' ? meta.ios_store_url : meta.android_store_url;
        // Without a store link there is no way out, so never block on it.
        if (!url) return;
        setStoreUrl(url);
        setRequired(belowMinimum);
      } catch {
        // The nudge must never get in the app's way.
      }
    })();
  }, []);

  if (storeUrl && required) {
    return (
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="required-update-title"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-navy text-white px-8 text-center"
      >
        <img
          src="/brand/iron-forums-logo-white-notag.png"
          alt="Iron Forums"
          className="h-14 w-auto mb-8"
        />
        <ArrowUpCircle size={40} className="text-gold mb-4" />
        <h1 id="required-update-title" className="text-2xl font-semibold mb-3">
          Please update the app
        </h1>
        <p className="text-white/80 max-w-sm mb-8">
          This version of Iron Forums is no longer supported. Update to the
          latest version to keep using the app.
        </p>
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-gold text-white font-semibold px-6 py-3 rounded-xl hover:bg-gold-dark transition"
        >
          {platform === 'ios' ? 'Update on the App Store' : 'Update on Google Play'}
        </a>
      </div>
    );
  }

  if (!storeUrl || dismissed) return null;

  return (
    <div className="fixed bottom-4 inset-x-4 z-50 flex justify-center pointer-events-none">
      <div className="pointer-events-auto flex items-center gap-3 bg-navy text-white rounded-xl shadow-xl px-4 py-3 max-w-md w-full">
        <ArrowUpCircle size={22} className="text-gold shrink-0" />
        <p className="text-sm flex-1">
          A new version of Iron Forums is available.
        </p>
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-gold text-white text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-gold-dark transition shrink-0"
        >
          Update
        </a>
        <button
          onClick={() => {
            sessionStorage.setItem(DISMISS_KEY, '1');
            setDismissed(true);
          }}
          aria-label="Dismiss update notice"
          className="text-white/60 hover:text-white transition shrink-0"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
